import React, { useState, useEffect, useMemo } from 'react'
import {
  Landmark,
  TrendingUp,
  Clock,
  DollarSign,
  Shield,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { Card } from '../ui/Card'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { calculateEscrowSummary, type EscrowData } from '../../utils/escrowCalculations'
import { useExchange } from '../../contexts/ExchangeContext'
import { useAuth } from '../../contexts/AuthContext'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatCurrencyPrecise(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

const STATUS_BADGES: Record<string, { label: string; color: string }> = {
  deposited: { label: 'Funds Deposited', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  pending_disbursement: { label: 'Pending Disbursement', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  partially_disbursed: { label: 'Partially Disbursed', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  disbursed: { label: 'Fully Disbursed', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400' },
}

export const EscrowPanel: React.FC = () => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  const [escrowData, setEscrowData] = useState<EscrowData | null>(null)
  const [showChart, setShowChart] = useState(false)

  useEffect(() => {
    const loadEscrow = () => {
      const exchangeId = currentExchange?.id || user?.id || 'anon'
      const raw = localStorage.getItem(`escrow_data_${exchangeId}`)
      if (raw) {
        try {
          setEscrowData(JSON.parse(raw))
        } catch {
          setEscrowData(null)
        }
      }
    }
    loadEscrow()
    window.addEventListener('salesHubCompleted', loadEscrow)
    window.addEventListener('wizardDataUpdated', loadEscrow)
    return () => {
      window.removeEventListener('salesHubCompleted', loadEscrow)
      window.removeEventListener('wizardDataUpdated', loadEscrow)
    }
  }, [currentExchange?.id, user?.id])

  const summary = useMemo(() => {
    if (!escrowData) return null
    return calculateEscrowSummary(escrowData)
  }, [escrowData])

  if (!summary) return null

  const statusBadge = STATUS_BADGES[summary.status] || STATUS_BADGES.deposited

  return (
    <Card className="p-5 dark:bg-slate-800 dark:border-slate-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Landmark className="h-4.5 w-4.5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">QI Escrow Account</h3>
            <p className="text-[10px] text-slate-500 dark:text-slate-400">
              Held at {(escrowData as { bankName?: string } | null)?.bankName || 'Western Alliance Bank'}
            </p>
          </div>
        </div>
        <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${statusBadge.color}`}>
          {statusBadge.label}
        </span>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {/* Current Balance */}
        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200/50 dark:border-emerald-800/30">
          <div className="flex items-center gap-1.5 mb-1">
            <DollarSign className="h-3 w-3 text-emerald-500" />
            <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Balance</span>
          </div>
          <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(summary.currentBalance)}</p>
        </div>

        {/* Interest Earned */}
        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200/50 dark:border-blue-800/30">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className="h-3 w-3 text-blue-500" />
            <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">Interest</span>
          </div>
          <p className="text-lg font-bold text-blue-700 dark:text-blue-300">+{formatCurrencyPrecise(summary.interestAccrued)}</p>
          <p className="text-[10px] text-blue-500 dark:text-blue-400">+{formatCurrencyPrecise(summary.dailyInterest)}/day</p>
          <p className="text-[10px] text-blue-500 dark:text-blue-400">@ {escrowData?.annualRatePercent ?? 3}% APY</p>
        </div>

        {/* Days in Escrow */}
        <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-200/50 dark:border-purple-800/30">
          <div className="flex items-center gap-1.5 mb-1">
            <Clock className="h-3 w-3 text-purple-500" />
            <span className="text-[10px] font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wider">Days Held</span>
          </div>
          <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{summary.daysInEscrow}</p>
          <p className="text-[10px] text-purple-500 dark:text-purple-400">@ {escrowData?.annualRatePercent ?? 3}% APY</p>
        </div>

        {/* Net Available */}
        <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200/50 dark:border-slate-600/30">
          <div className="flex items-center gap-1.5 mb-1">
            <Shield className="h-3 w-3 text-slate-500 dark:text-slate-400" />
            <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Net Available</span>
          </div>
          <p className="text-lg font-bold text-slate-700 dark:text-slate-200">{formatCurrency(summary.netAvailable)}</p>
          <p className="text-[10px] text-slate-400 dark:text-slate-500">QI fees: {formatCurrency(summary.qiFees)}</p>
        </div>
      </div>

      {/* Fee Breakdown */}
      <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-700/30 text-xs mb-3">
        <div className="flex items-center gap-4">
          <span className="text-slate-500 dark:text-slate-400">
            Principal: <strong className="text-slate-700 dark:text-slate-200">{formatCurrency(summary.principalAmount)}</strong>
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            QI Fee: <strong className="text-slate-700 dark:text-slate-200">${escrowData?.qiFeeFlat ?? 750} + {escrowData?.qiFeePercent ?? 0.1}%</strong>
          </span>
          {summary.totalDisbursed > 0 && (
            <span className="text-slate-500 dark:text-slate-400">
              Disbursed: <strong className="text-slate-700 dark:text-slate-200">{formatCurrency(summary.totalDisbursed)}</strong>
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowChart(!showChart)}
          className="flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
        >
          {showChart ? 'Hide' : 'Show'} chart
          {showChart ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </div>

      {/* Balance Chart */}
      {showChart && (
        <div className="h-40 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={summary.balanceHistory}
              margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
            >
              <defs>
                <linearGradient id="escrowGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="escrowProjectedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.08} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.15)" />
              <XAxis
                dataKey="day"
                tick={{ fill: 'rgb(148, 163, 184)', fontSize: 10 }}
                tickFormatter={(day) => `Day ${day}`}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: 'rgb(148, 163, 184)', fontSize: 10 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                domain={['dataMin - 1000', 'dataMax + 1000']}
              />
              <Tooltip
                cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '3 3' }}
                contentStyle={{
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid rgb(51, 65, 85)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
                }}
                labelStyle={{ color: 'rgb(241, 245, 249)', fontSize: 11, fontWeight: 600, marginBottom: 4 }}
                itemStyle={{ color: 'rgb(110, 231, 183)', fontSize: 12 }}
                formatter={(value: number, _name: string, entry: { payload?: { projected?: boolean } }) => [
                  `${formatCurrencyPrecise(value)}${entry.payload?.projected ? ' (projected)' : ''}`,
                  'Balance',
                ]}
                labelFormatter={(day) => `Day ${day}`}
              />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="0"
                fill="url(#escrowGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
          {summary.daysInEscrow === 0 && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center mt-1">
              Projected 180-day interest growth
            </p>
          )}
        </div>
      )}
    </Card>
  )
}
